import React, { useState } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, LayoutTemplate } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { RitualFormContent } from './components/RitualFormContent';
import { RitualRepetition, useCreateRitualMutation, TotadminPermission } from 'data/__generated__';
import { RitualPopup } from 'pages/Rituals/components/RitualPopup';

type FormData = {
    activeDate: string;
    activeWeekdays: boolean[];
    associatedDiscount: number;
    billToOrganizationId: string | null;
    expiringProductsOnly: boolean;
    image: File;
    messagesSetId: string | null;
    name: string;
    repetition: RitualRepetition;
    requiresParticipation: boolean;
    resetsBetweenPurchases: boolean;
    restrictedBadgeIds: string[];
    restrictedCategoryIds: string[];
    restrictedOrganizationIds: string[];
    restrictedProductIds: string[];
    restrictedSiteIds: string[];
    restrictedSubcategoryIds: string[];
    restrictedTagIds: string[];
    restrictedUserIds: string;
    timeWindow: {
        start: string;
        end: string;
    };
    supplierPartnershipId: string | null;
};

export const RitualCreate = () => {
    const navigate = useNavigate();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [chosenTemplate, setChosenTemplate] = useState<{ name: string; imageUrl: string } | null>(null);
    const [createRitual, { loading: createRitualLoading }] = useCreateRitualMutation();

    const methods = useForm<FormData>({
        defaultValues: {
            billToOrganizationId: null,
            repetition: RitualRepetition.Weekly,
            restrictedBadgeIds: [],
            restrictedCategoryIds: [],
            restrictedOrganizationIds: [],
            restrictedProductIds: [],
            restrictedSiteIds: [],
            restrictedSubcategoryIds: [],
            restrictedTagIds: [],
            restrictedUserIds: '',
            supplierPartnershipId: null,
        },
    });

    const {
        handleSubmit,
        formState: { isDirty },
    } = methods;

    const onSubmit = handleSubmit(async (fields) => {
        const { activeDate, activeWeekdays, repetition } = fields;
        const variables = {
            createRitualInput: {
                ...fields,
                activeDate: repetition === RitualRepetition.Punctual ? activeDate : null,
                activeWeekdays: repetition === RitualRepetition.Weekly ? activeWeekdays : null,
                restrictedUserIds: fields.restrictedUserIds ? fields.restrictedUserIds.trim().split(',') : [],
                imageUrl: chosenTemplate?.imageUrl,
            },
        };
        const { data } = await createRitual({
            variables,
        });

        if (data) {
            const {
                createRitual: { createdRitual, success, errors },
            } = data;
            if (success && createdRitual) {
                toast.success('Le rituel a bien été créé !');
                navigate(`/ritual/${createdRitual._id}`);
            } else {
                if (errors) {
                    errors.forEach((error) => {
                        toast.error(error, { autoClose: false });
                    });

                    if (errors.length > 1) {
                        toast.info('Cliquez pour fermer toutes les notifications', {
                            autoClose: false,
                            onClick: () => toast.dismiss(),
                        });
                    }
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Rituel',
            items: [{ label: 'Modèle', icon: <LayoutTemplate />, onClick: () => setIsPopupOpen(true) }],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/rituals' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.ritualCreate} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={createRitualLoading}
                            saveTitle="Créer"
                            saveRequiredPermissions={[TotadminPermission.RitualsManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <RitualFormContent chosenTemplate={chosenTemplate} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
            <RitualPopup isOpen={isPopupOpen} setIsOpen={setIsPopupOpen} setChosenTemplate={setChosenTemplate} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
